class ShapeAlignmentError extends Error {
  constructor (shape1, shape2, axis) {
    super(`Shapes (${shape1.join(',')}) and (${shape2.join(',')}) do not align on axis ${axis}`)
  }
}

export default class Vector {
  constructor (list) {
    this.list = [...list]
    this.build(list)
  }

  sum () {
    return this.list.reduce((a, b) => a + b, 0)
  }

  avg () {
    return this.list.reduce((a, b) => a + b, 0) / this.list.length
  }

  __add__ (right) {
    return this.elementWise(right, (a, b) => {
      if (a instanceof Vector) {
        return a.__add__(b)
      }
      return a + b
    })
  }

  __sub__ (right) {
    return this.elementWise(right, (a, b) => {
      if (a instanceof Vector) {
        return a.__sub__(b)
      }
      return a - b
    })
  }

  __mult__ (right) {
    return this.elementWise(right, (a, b) => {
      if (a instanceof Vector) {
        return a.__mult__(b)
      }
      return a * b
    })
  }

  __div__ (right) {
    return this.elementWise(right, (a, b) => {
      if (a instanceof Vector) {
        return a.__div__(b)
      }
      return a / b
    })
  }

  elementWise (right, coreOp) {
    if (right instanceof Vector) {
      this.checkShape(right)
    }

    return new Vector(this.list.map((x, i) => {
      const y = right instanceof Vector ? right.list[i] : right
      return coreOp(x, y)
    }))
  }

  checkShape (right, axis = 0) {
    if (this.shape[axis] !== right.shape[axis]) {
      throw new ShapeAlignmentError(this.shape, right.shape, axis)
    }
  }

  cast (arr) {
    if (Array.isArray(arr)) {
      return new Vector(arr)
    }
    return arr
  }

  build (list) {
    const dimension = list.length

    let innerShape = [] // [] means: we do not yet know what the inner shape is.
    let innerIsVectors = null // null means: we do not yet know what the inner types are.

    for (let i = 0; i < dimension; i++) {
      const inner = this.cast(list[i])
      this.list[i] = inner

      if (innerIsVectors === null) {
        innerIsVectors = inner instanceof Vector
      }

      // Check type consistency
      if ((inner instanceof Vector && !innerIsVectors) || (!(inner instanceof Vector) && innerIsVectors)) {
        // Type inconsistency found
        innerShape = [null]
        continue
      }

      // Check shape consistency
      if (inner instanceof Vector) {
        if (innerShape.length === 0) {
          // Inner shape was not set before and inner is a vector
          innerShape = inner.shape
        }

        if (innerShape.length > 0 && !this.shapesEqual(innerShape, inner.shape)) {
          // Shape inconsistency found
          innerShape = [null]
        }
      }
    }

    this.shape = [dimension, ...innerShape]
  }

  shapesEqual (shape1, shape2) {
    if (shape1.length !== shape2.length) {
      return false
    }
    let equal = true
    shape1.forEach((x, i) => {
      if (x !== shape2[i]) {
        equal = false
      }
    })
    return equal
  }
}
